import { c as _c } from "react/compiler-runtime";
import { cn } from '@/utils/shad';
import { Chip } from '@/comps/chip';
import { ArrowLeft, FilterIcon, Loader2, Save, X } from 'lucide-react';
import { translateSafe } from '@/utils/translations';
import { PopoverClose } from '@/comps/primitives/popover';
import { useCallback, useState } from 'react';
import { WithClassName } from '@/types/component';
import { GenericFilter } from './FilterPicker';
import { Button } from '../primitives/button';
import { useDialogActions, useDialogs } from '@/contexts/dialogs/hooks';
import { Input } from '../primitives/input';
import { Tabs, TabsContent } from '../primitives/tabs';
import { useProjectsContext } from '@/providers/projects-provider';
import { FilterGroup } from '@/api/generated/model';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEY_SAVED_FILTERS } from '@/constants/query-keys';
import { Switch } from '../primitives/switch';
import { DialogContent, DialogDescription, DialogTitle } from '../primitives/dialog';
import { useAuthContext } from '@/hooks/use-auth';
import { SAVE_FILTERS_REQUIRES_WRITE_MESSAGE } from './utils';
import { useUpsertFilterGroup } from '@/api/generated/projects/projects';
type FilterPickerHeaderProps = WithClassName & {
  filters: GenericFilter[];
  showFilters?: boolean;
  showFilterBy?: boolean;
  filterKinds?: string[];
  filterKind?: string | null;
  setFilterKind?: (v: string | null) => void;
  isPopover?: boolean;
  label?: string;
  type?: 'filter' | 'breakdown';
  hideSaveButton?: boolean;
  hideBackButton?: boolean;
  onBack?: () => void;
};
export function FilterPickerHeader(t0) {
  const $ = _c(28);
  const {
    showFilters: t1,
    showFilterBy: t2,
    filterKinds,
    filterKind,
    setFilterKind,
    isPopover,
    className,
    label,
    type: t3,
    filters,
    hideSaveButton: t4,
    hideBackButton: t5,
    onBack
  } = t0;
  const showFilters = t1 === undefined ? true : t1;
  const showFilterBy = t2 === undefined ? true : t2;
  const type = t3 === undefined ? "filter" : t3;
  const hideSaveButton = t4 === undefined ? false : t4;
  const hideBackButton = t5 === undefined ? false : t5;
  const showBorder = showFilters && (filterKinds?.length ?? 0) > 0;
  const dialogs = useDialogs();
  const {
    savedFilters,
    projectId
  } = useProjectsContext();
  const {
    canWrite
  } = useAuthContext();
  const t6 = showBorder && "border-b";
  let t7;
  if ($[0] !== className || $[1] !== t6) {
    t7 = cn("flex items-center justify-between bg-background px-3", t6, className);
    $[0] = className;
    $[1] = t6;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  let t8;
  if ($[3] !== label || $[4] !== showFilterBy || $[5] !== type) {
    t8 = showFilterBy && <Chip size="small" className="flex h-7 items-center gap-2 rounded-base border bg-background px-2 py-1 font-semibold"><FilterIcon className="inline" size={12} /><span>{label || type === "filter" ? translateSafe("filter", "filterBy") : translateSafe("filter", "groupBy")}</span></Chip>;
    $[3] = label;
    $[4] = showFilterBy;
    $[5] = type;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== canWrite || $[8] !== dialogs || $[9] !== filters || $[10] !== hideSaveButton || $[11] !== projectId || $[12] !== savedFilters) {
    t9 = filters.length > 0 && !hideSaveButton && <Button size="sm" variant="outline" className="h-7 rounded-base border bg-background px-2 py-1 font-semibold" PrependIcon={Save} disabled={!canWrite} disabledMessage={SAVE_FILTERS_REQUIRES_WRITE_MESSAGE} onClick={() => {
      if (!projectId) {
        toast.error("No project selected");
        return;
      }
      dialogs.push({
        content: <SaveFiltersDialog savedFilters={savedFilters} projectId={projectId} filters={filters} />
      });
    }}>Save filters</Button>;
    $[7] = canWrite;
    $[8] = dialogs;
    $[9] = filters;
    $[10] = hideSaveButton;
    $[11] = projectId;
    $[12] = savedFilters;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== filterKind || $[15] !== hideBackButton || $[16] !== isPopover || $[17] !== onBack || $[18] !== setFilterKind || $[19] !== type) {
    t10 = !filterKind || type === "breakdown" ? isPopover ? !hideBackButton && <PopoverClose asChild={true}><button className="flex h-7 items-center justify-center rounded-base border p-1.5 leading-4 transition-colors hover:cursor-pointer hover:bg-neutral-800"><X size={14} /></button></PopoverClose> : <></> : !hideBackButton && <button className="flex h-7 items-center justify-center rounded-base border p-1.5 leading-4" onClick={() => onBack ? onBack() : setFilterKind?.(null)}><ArrowLeft size={14} /></button>;
    $[14] = filterKind;
    $[15] = hideBackButton;
    $[16] = isPopover;
    $[17] = onBack;
    $[18] = setFilterKind;
    $[19] = type;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t9) {
    t11 = <div className="flex items-center gap-2">{t9}{t10}</div>;
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t7 || $[26] !== t8) {
    t12 = <div className={t7}>{t8}{t11}</div>;
    $[24] = t11;
    $[25] = t7;
    $[26] = t8;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}
export function SaveFiltersDialog(t0) {
  const $ = _c(50);
  const {
    savedFilters,
    projectId,
    filters
  } = t0;
  const {
    close
  } = useDialogActions();
  const [activeTab, setActiveTab] = useState("new");
  const queryClient = useQueryClient();
  const [selectedFilterGroup, setSelectedFilterGroup] = useState(null);
  const [name, setName] = useState("");
  let t1;
  if ($[0] !== close || $[1] !== projectId || $[2] !== queryClient) {
    t1 = {
      mutation: {
        onSuccess: () => {
          toast.success("Filters saved");
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_SAVED_FILTERS, projectId]
          });
          close();
        },
        onError: _temp
      }
    };
    $[0] = close;
    $[1] = projectId;
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    mutate: upsertFilterGroup,
    isLoading
  } = useUpsertFilterGroup(t1);
  const mapToModel = _temp3;
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DialogTitle>Save filters</DialogTitle>;
    t3 = <DialogDescription>Save your current filters for quick access later. Saved filters can be reused across reports.</DialogDescription>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== activeTab || $[7] !== savedFilters.length) {
    t4 = savedFilters.length > 0 ? <div className="flex items-center gap-2"><p>Save New</p><Switch checked={activeTab === "existing"} onCheckedChange={() => setActiveTab(activeTab === "existing" ? "new" : "existing")} /><p>Update Existing</p></div> : <div />;
    $[6] = activeTab;
    $[7] = savedFilters.length;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e => setName(e.target.value);
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== name) {
    t6 = <Input placeholder="Name" value={name} onChange={t5} />;
    $[10] = name;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = isLoading || !name || name.length < 4;
  const t8 = isLoading ? Loader2 : undefined;
  let t9;
  if ($[12] !== filters || $[13] !== name || $[14] !== projectId || $[15] !== upsertFilterGroup) {
    t9 = () => upsertFilterGroup({
      projectId,
      data: {
        name,
        filters: mapToModel(filters)
      }
    });
    $[12] = filters;
    $[13] = name;
    $[14] = projectId;
    $[15] = upsertFilterGroup;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = <div className="flex w-full items-center justify-end gap-2"><Button disabled={t7} PrependIcon={t8} iconClassName="animate-spin" onClick={t9}>Save</Button></div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t6) {
    t11 = <TabsContent value="new"><div className="flex flex-col gap-2">{t6}{t10}</div></TabsContent>;
    $[21] = t10;
    $[22] = t6;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== savedFilters || $[25] !== selectedFilterGroup?.id) {
    let t13;
    if ($[27] !== selectedFilterGroup?.id) {
      t13 = x_0 => <Button key={x_0.id} variant="outline" onClick={() => setSelectedFilterGroup(x_0)} className={cn("h-9", selectedFilterGroup?.id === x_0.id && "bg-neutral-800")}>{x_0.name}</Button>;
      $[27] = selectedFilterGroup?.id;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    t12 = savedFilters.map(t13);
    $[24] = savedFilters;
    $[25] = selectedFilterGroup?.id;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[29] !== t12) {
    t13 = <div className="flex max-h-75 flex-col gap-2 overflow-y-auto">{t12}</div>;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = !selectedFilterGroup || isLoading;
  const t15 = isLoading ? Loader2 : undefined;
  let t16;
  if ($[31] !== filters || $[32] !== projectId || $[33] !== selectedFilterGroup || $[34] !== upsertFilterGroup) {
    t16 = () => {
      if (!selectedFilterGroup) {
        toast.error("No filter group selected");
        return;
      }
      upsertFilterGroup({
        projectId,
        data: {
          ...selectedFilterGroup,
          filters: mapToModel(filters)
        }
      });
    };
    $[31] = filters;
    $[32] = projectId;
    $[33] = selectedFilterGroup;
    $[34] = upsertFilterGroup;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t14 || $[37] !== t15 || $[38] !== t16) {
    t17 = <div className="flex w-full items-center justify-end gap-2"><Button disabled={t14} PrependIcon={t15} iconClassName="animate-spin" onClick={t16}>Update</Button></div>;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t13 || $[41] !== t17) {
    t18 = <TabsContent value="existing"><div className="flex flex-col gap-2">{t13}{t17}</div></TabsContent>;
    $[40] = t13;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== activeTab || $[44] !== t11 || $[45] !== t18) {
    t19 = <Tabs value={activeTab}>{t11}{t18}</Tabs>;
    $[43] = activeTab;
    $[44] = t11;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t19 || $[48] !== t4) {
    t20 = <DialogContent>{t2}{t3}{t4}{t19}</DialogContent>;
    $[47] = t19;
    $[48] = t4;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  return t20;
}
function _temp3(filters_0) {
  return filters_0.map(_temp2);
}
function _temp2(x) {
  return {
    column: x.column,
    values: x.values,
    is_excluded: x.is_exclude ?? false,
    search: x.search ?? null
  };
}
function _temp(error) {
  toast.error("Failed to save filters");
  console.error(error);
}